/** Formateadores compartidos para rich content. */

import type { RichColumnFormat } from "@/lib/caonabo/rich-content";

/**
 * Diccionario de status / valores enum comunes que vienen del backend
 * en inglés/snake_case. La UI debe presentarse SIEMPRE en español.
 * Si llega un valor que no está aquí, se respeta tal cual.
 */
const VALUE_TRANSLATIONS: Record<string, string> = {
  // Estados generales
  active: "Activo",
  inactive: "Inactivo",
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  cancelled: "Anulado",
  canceled: "Anulado",
  completed: "Completado",
  draft: "Borrador",
  // Estados Caonabo / agrícola
  pre_orden: "Pre-Orden",
  "pre-orden": "Pre-Orden",
  confirmada: "Confirmada",
  en_camino: "En camino",
  en_ruta: "En ruta",
  programado: "Programado",
  resuelta: "Resuelta",
  emergencia: "Emergencia",
  borrador: "Borrador",
  // Pagos
  paid: "Pagada",
  unpaid: "Sin pagar",
  partial: "Parcial",
  partially_paid: "Pago parcial",
  overdue: "Vencida",
  // Métodos
  cash: "Efectivo",
  efectivo: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
  transferencia: "Transferencia",
  check: "Cheque",
  // Severidad
  high: "Alta",
  medium: "Media",
  low: "Baja",
  urgent: "Urgente",
  normal: "Normal",
  // Movimientos
  in: "Entrada",
  out: "Salida",
  entrada: "Entrada",
  salida: "Salida",
  ajuste: "Ajuste",
  adjustment: "Ajuste",
  // Modalidad / unidad
  saco: "Saco",
  caja: "Caja",
  qq: "qq",
  // Sí/No
  yes: "Sí",
  no: "No",
  true: "Sí",
  false: "No",
  // Otros
  unknown: "Desconocido",
  none: "Ninguno",
  null: "—",
};

export function translateValue(s: string): string {
  if (!s) return s;
  const key = s.trim().toLowerCase();
  return VALUE_TRANSLATIONS[key] ?? s;
}

export function formatValue(value: unknown, format?: RichColumnFormat): string {
  if (value === null || value === undefined || value === "") return "—";
  if ((format === "money" || format === "currency") && typeof value !== "boolean") {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `RD$ ${n.toLocaleString("es-DO", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
  if (format === "qq") {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return `${n.toLocaleString("es-DO", { maximumFractionDigits: 2 })} qq`;
  }
  if (format === "percent") {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    // Si llega como fracción 0.42 → 42%; si llega como 42 → 42%
    const pct = Math.abs(n) <= 1 ? n * 100 : n;
    return `${pct.toFixed(1)}%`;
  }
  if (format === "number") {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toLocaleString("es-DO", { maximumFractionDigits: 2 });
  }
  if (format === "date") {
    if (value instanceof Date) {
      return value.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric" });
    }
    if (typeof value === "string") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric" });
      }
      return value.slice(0, 10);
    }
    return String(value);
  }
  if (format === "datetime") {
    if (value instanceof Date) {
      return value.toLocaleString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    }
    if (typeof value === "string") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleString("es-DO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
      }
      return value.slice(0, 16).replace("T", " ");
    }
    return String(value);
  }
  if (format === "status") {
    return translateValue(String(value));
  }
  // Sin format: detección por tipo
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value === "number") {
    return value.toLocaleString("es-DO", { maximumFractionDigits: 2 });
  }
  if (typeof value === "string") return translateValue(value);
  return String(value);
}

/** Alineación recomendada por formato. Numéricos a la derecha. */
export function alignFor(format?: RichColumnFormat): "left" | "right" {
  if (format === "money" || format === "currency" || format === "qq" || format === "number" || format === "percent") {
    return "right";
  }
  return "left";
}

/** Color del badge por estado conocido. */
export function statusColor(status: string): { bg: string; color: string; border: string } {
  const s = status.toLowerCase();
  if (s.includes("recibida") || s.includes("completado") || s.includes("pagada") || s.includes("activa") || s.includes("activo") || s.includes("done") || s.includes("aprobado") || s.includes("approved"))
    return { bg: "rgba(34,197,94,0.12)", color: "#22c55e", border: "rgba(34,197,94,0.30)" };
  if (s.includes("cancelada") || s.includes("cancelado") || s.includes("anulada") || s.includes("error") || s.includes("rechazado") || s.includes("rejected"))
    return { bg: "rgba(239,68,68,0.12)", color: "#ef4444", border: "rgba(239,68,68,0.30)" };
  if (s.includes("pendiente") || s.includes("borrador") || s.includes("pre-orden") || s.includes("pre orden") || s.includes("programado") || s.includes("draft") || s.includes("pending"))
    return { bg: "rgba(245,158,11,0.12)", color: "#f59e0b", border: "rgba(245,158,11,0.30)" };
  if (s.includes("en ruta") || s.includes("en camino") || s.includes("en proceso") || s.includes("parcial") || s.includes("confirmada"))
    return { bg: "rgba(96,165,250,0.12)", color: "#60a5fa", border: "rgba(96,165,250,0.30)" };
  if (s.includes("vencida") || s.includes("emergencia") || s.includes("detenida") || s.includes("overdue"))
    return { bg: "rgba(239,68,68,0.10)", color: "#fca5a5", border: "rgba(239,68,68,0.30)" };
  return { bg: "rgba(148,163,184,0.10)", color: "#94a3b8", border: "rgba(148,163,184,0.25)" };
}

/** Color del texto por nombre de columna + valor. Reglas semánticas. */
export function semanticColor(columnKey: string, value: unknown): string {
  if (typeof value === "number") {
    const k = columnKey.toLowerCase();
    if (k.includes("vencid") || k.includes("overdue") || k.includes("dias_vencid")) {
      return value > 0 ? "color:#ef4444; font-weight:600" : "";
    }
    if (k.includes("pendiente") || k.includes("balance")) {
      return value > 0 ? "color:#f59e0b; font-weight:500" : "";
    }
    if (k.includes("pagad") || k.includes("paid") || k.includes("cobrad") || k.includes("ingreso") || k.includes("revenue")) {
      return value > 0 ? "color:#22c55e; font-weight:500" : "";
    }
  }
  if (typeof value === "string") {
    const v = value.toLowerCase();
    if (["pendiente", "pending", "vencido", "overdue", "cancelado", "cancelled", "rejected", "rechazado"].some((x) => v.includes(x))) {
      return "color:#f59e0b; font-weight:500";
    }
    if (["pagado", "paid", "completado", "completed", "active", "activo", "approved", "aprobado", "resuelta"].some((x) => v.includes(x))) {
      return "color:#22c55e; font-weight:500";
    }
  }
  return "";
}
